/**
 * Set-new-password screen (INVENTORY §1 screen 2, §3; prototype template lines
 * 68-79). Rendered with NO props and WITHOUT header / bottom nav.
 *
 * TWO WAYS IN, ONE SCREEN (D-08). `doLogin()` on a `mustReset` account, which
 * is the prototype's only route; and `boot()` on a password-reset link, which
 * the prototype has no concept of. The screen never decides which for itself —
 * it renders `loginVM().resetHead` / `.resetBody`, and the store sets
 * `resetMode`. Hardcoding FIRST LOGIN here is what would greet somebody who has
 * used the app for a year as though they had just arrived.
 *
 * There is no way back to the login form short of a reload, in either mode
 * (faithful to the prototype, and correct for recovery: leaving without setting
 * a password spends the link for nothing).
 *
 * DEVIATION D-01: the floor is 8 characters. The copy is never hardcoded here —
 * `doReset()` writes `resetErr` from `PASSWORD_MIN_COPY`, and `loginVM()`
 * re-exports the same string as `resetHint` for anywhere a hint is wanted.
 */

import type { CSSProperties } from 'react';

import { PixelButton } from '../components/PixelButton.tsx';
import { useQd } from '../store/index.ts';
import { loginVM } from '../store/selectors.ts';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '13px',
  padding: '12px',
  outline: 'none',
};

export default function Reset(): JSX.Element {
  const s = useQd();
  const v = loginVM(s);

  return (
    <div
      data-screen-label="Set new password"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 14px',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          background: 'var(--gt-card)',
          border: '1px solid var(--gt-border)',
          boxShadow: 'var(--gt-shadow-panel)',
          padding: '28px 20px 24px',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '10px',
            color: 'var(--gt-text)',
            lineHeight: 1.8,
          }}
        >
          {v.resetHead}
        </div>
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '10.5px',
            lineHeight: 1.7,
            color: 'var(--gt-text-5)',
          }}
        >
          {v.resetBody}
        </div>
        <input
          type="password"
          value={v.rp1}
          onChange={(e) => s.setRp1(e.target.value)}
          placeholder="NEW PASSWORD"
          style={FIELD}
        />
        <input
          type="password"
          value={v.rp2}
          onChange={(e) => s.setRp2(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.repeat) void s.doReset();
          }}
          placeholder="CONFIRM PASSWORD"
          style={FIELD}
        />
        {v.hasResetErr ? (
          <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '11px', color: 'var(--acc2)' }}>
            ✕ <span>{v.resetErr}</span>
          </div>
        ) : null}
        <PixelButton
          label={v.resetLbl}
          busy={v.resetBusy}
          onClick={() => void s.doReset()}
          fs={8.5}
          pad="15px 14px"
          center
        />
      </div>
    </div>
  );
}
